import { useEffect, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button } from './Button'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
  closeOnOverlay?: boolean
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlay = true,
}: ModalProps) {
  const { t: tc } = useTranslation('common')
  const dialogRef = useRef<HTMLDialogElement>(null)
  const modalBoxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen) {
      dialog.showModal()
      // DAT-2 (T149-093): a modal reused across content (e.g. re-opened for a
      // different row) kept whatever scroll position the LAST open left the
      // .modal-box at, so a tall modal could reopen already scrolled past its
      // own top. Reset on every open, not just first mount.
      if (modalBoxRef.current) modalBoxRef.current.scrollTop = 0
    } else {
      dialog.close()
    }
  }, [isOpen])

  // Escape key: the browser fires the native <dialog> 'cancel' event and closes
  // the dialog in the DOM WITHOUT calling onClose — which leaves the parent's
  // isOpen state stuck `true`, so the modal can't be reopened (B-011). Route the
  // Escape close through React: preventDefault() stops the native close, then
  // onClose() flips isOpen and the effect above calls dialog.close() cleanly.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    const handleCancel = (e: Event) => {
      e.preventDefault()
      onClose()
    }
    dialog.addEventListener('cancel', handleCancel)
    return () => dialog.removeEventListener('cancel', handleCancel)
  }, [onClose])

  // From sm up only (2026-10-05): below it the modal is DaisyUI's bottom
  // sheet, full width by design (modal-bottom sets max-width: none). A cap
  // there left a small sheet narrower than a 393px phone, and modal-bottom's
  // place-items: end pushed it right with a gap on the left.
  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
  }

  return (
    <dialog
      ref={dialogRef}
      className={`modal modal-bottom sm:modal-middle`}
      onClick={(e) => {
        if (closeOnOverlay && e.target === dialogRef.current) {
          onClose()
        }
      }}
    >
      <div
        ref={modalBoxRef}
        className={`modal-box rounded-t-2xl sm:rounded-2xl pb-[max(env(safe-area-inset-bottom),1rem)] ${sizeClasses[size]}`}
      >
        {title && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">{title}</h3>
            <Button variant="ghost" size="sm" shape="circle" onClick={onClose} aria-label={tc('actions.close')}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
        <div>{children}</div>
        {footer && <div className="modal-action">{footer}</div>}
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  )
}
